import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";

import type {
  CharacteristicGroup,
  ComparisonDataService,
  ComparisonObject,
} from "./comparison-data-service";
import { getCharacteristicGroups } from "./comparison-utils";

export interface GroupedCategory {
  formattedName: string;
  objectCategories: Map<string, CharacteristicGroup>;
}

export type StoredObject = ComparisonObject & { key: string };

function objectKey(obj: ComparisonObject): string {
  return `${obj.name.value}-${obj.type?.value ?? ""}`;
}

function toStoredObject(obj: ComparisonObject): StoredObject {
  return { ...obj, key: objectKey(obj) };
}

export const useComparisonStore = defineStore("comparison", () => {
  const allVizObjects = ref<StoredObject[]>([]);
  const selectedObjectKeys = ref<Set<string>>(new Set());
  const isBusy = ref(false);
  const error = ref<string | null>(null);
  const dataService = shallowRef<ComparisonDataService>();

  const visibleObjects = computed(() =>
    allVizObjects.value.filter((o) => selectedObjectKeys.value.has(o.key)),
  );

  const unselectedObjects = computed(() =>
    allVizObjects.value.filter((o) => !selectedObjectKeys.value.has(o.key)),
  );

  const subjectObject = computed(() => {
    return visibleObjects.value[0];
  });

  const referenceObjects = computed(() => {
    if (!subjectObject.value) return [];
    return visibleObjects.value.filter((obj) => obj !== subjectObject.value);
  });

  const categoryNames = computed(() => {
    const names = new Set<string>();
    for (const obj of visibleObjects.value) {
      for (const cat of getCharacteristicGroups(obj.characteristics?.value)) {
        names.add(cat.Formatted_name);
      }
    }
    return [...names];
  });

  const groupedCategories = computed<GroupedCategory[]>(() => {
    return categoryNames.value.map((name) => {
      const objectCategories = new Map<string, CharacteristicGroup>();
      for (const obj of visibleObjects.value) {
        const category = getCharacteristicGroups(
          obj.characteristics?.value,
        ).find((c) => c.Formatted_name === name);
        if (category) {
          objectCategories.set(obj.name.value, category);
        }
      }
      return { formattedName: name, objectCategories };
    });
  });

  function setAllVizObjects(incoming: ComparisonObject[]) {
    // Merge: keep existing objects in their current order, append genuinely new ones at the end
    const incomingByName = new Map(incoming.map((o) => [o.name.value, o]));
    const existing = allVizObjects.value.filter((o) =>
      incomingByName.has(o.name.value),
    );
    const existingNames = new Set(existing.map((o) => o.name.value));
    const added = incoming
      .filter((o) => !existingNames.has(o.name.value))
      .map(toStoredObject);

    // Sort merged list: Objecten before Referenties
    const merged = [...existing, ...added];
    allVizObjects.value = [
      ...merged.filter((o) => o.type?.value !== "Referenties"),
      ...merged.filter((o) => o.type?.value === "Referenties"),
    ];

    // Drop any selected keys that are no longer present
    const allKeys = new Set(allVizObjects.value.map((o) => o.key));
    const pruned = new Set(
      [...selectedObjectKeys.value].filter((k) => allKeys.has(k)),
    );
    if (pruned.size !== selectedObjectKeys.value.size) {
      selectedObjectKeys.value = pruned;
    }
  }

  function addObject(key: string) {
    selectedObjectKeys.value = new Set([...selectedObjectKeys.value, key]);
  }

  function removeObject(key: string) {
    const next = new Set(selectedObjectKeys.value);
    next.delete(key);
    selectedObjectKeys.value = next;
  }

  function moveObject(keyA: string, keyB: string) {
    const items = [...allVizObjects.value];
    const fromIndex = items.findIndex((o) => o.key === keyA);
    const toIndex = items.findIndex((o) => o.key === keyB);
    if (fromIndex < 0 || toIndex < 0) return;
    if (items[fromIndex].type?.value !== items[toIndex].type?.value) return;
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    allVizObjects.value = items;
  }

  function setDataService(service: ComparisonDataService) {
    dataService.value = service;
  }

  async function initialize() {
    if (!dataService.value) {
      error.value = "No data service configured";
      return;
    }

    isBusy.value = true;
    error.value = null;

    try {
      dataService.value.onVizDataLoaded((objects) => {
        setAllVizObjects(objects);
      });

      await dataService.value.initialize();
    } catch (e) {
      error.value =
        e instanceof Error ? e.message : "Failed to initialize comparison data";
    } finally {
      isBusy.value = false;
    }
  }

  return {
    allVizObjects,
    visibleObjects,
    unselectedObjects,
    isBusy,
    error,
    subjectObject,
    referenceObjects,
    categoryNames,
    groupedCategories,
    addObject,
    removeObject,
    setAllVizObjects,
    moveObject,
    setDataService,
    initialize,
  };
});
